import { useCallback } from 'react';

import { twMerge } from 'tailwind-merge';

import { Spinner } from '../../../..';
import { ChevronIcon } from '../../../../../assets';
import { EModalIds, openModal } from '../../../../../lib';
import { FloatingLabel } from '../../../floating-label';
import { SelectOption, SelectProps } from '../../types';
import { SelectQueryOptions } from '../query-options';
import { SelectOptions } from '../select-options';

export const Select = <TOption, TResponse = unknown>(
  props: SelectProps<TOption, TResponse>,
) => {
  const {
    value,
    onSelect,
    renderTriggerValue,
    queryProps,
    modalKey = EModalIds.SelectModal,
    isMulti = false,
    triggerProps = {},
    options = [],
    searchProps,
    infinityProps,
    modalProps,
    listProps,
    renderOptionItem,
    CustomEmptyState,
    CustomSkeletons,
    isClearable,
    className,
    classNames,
    placeholder,
    disabled,
    loading,
    error,
    isInvalid,
    variant,
    description,
  } = props;

  const valueLabel = value && Array.isArray(value) ? value[0] : (value ?? null);

  const mergedTriggerProps = {
    placeholder: placeholder ?? triggerProps.placeholder,
    disabled: disabled ?? triggerProps.disabled,
    loading: loading ?? triggerProps.loading,
    isInvalid: (isInvalid ?? error) ? true : triggerProps.isInvalid,
    variant: variant ?? triggerProps.variant,
    description: description ?? error ?? triggerProps.description ?? '',
    ...triggerProps,
  };

  const selectOptionHandler = useCallback(
    (selectedValue: SelectOption<TOption>[]) => {
      if (!selectedValue?.length) {
        if (isMulti) {
          (onSelect as (value: SelectOption<TOption>[]) => void)([]);
        } else {
          (onSelect as (value: SelectOption<TOption> | null) => void)(null);
        }
        return;
      }

      if (isMulti) {
        (onSelect as (value: SelectOption<TOption>[]) => void)(selectedValue);
      } else {
        (onSelect as (value: SelectOption<TOption> | null) => void)(
          selectedValue[0],
        );
      }
    },
    [isMulti, onSelect],
  );

  const openSelectModalHandler = (event: React.MouseEvent<HTMLElement>) => {
    if (mergedTriggerProps.disabled || mergedTriggerProps.loading) return;
    event.preventDefault();

    const normalizedValue: SelectOption<TOption>[] = value
      ? Array.isArray(value)
        ? value
        : [value]
      : [];

    const modalComponentProps = {
      value: normalizedValue,
      modalId: modalKey,
      options: options ?? [],
      onSelect: selectOptionHandler,
      searchProps,
      infinityProps,
      modalProps,
      listProps,
      renderOptionItem,
      CustomEmptyState,
      CustomSkeletons,
      isClearable,
      isMulti,
      className,
      classNames,
    };

    openModal({
      id: modalKey,
      component: () =>
        queryProps ? (
          <SelectQueryOptions
            modalId={modalKey}
            queryParams={queryProps}
            optionsProps={modalComponentProps}
          />
        ) : (
          <SelectOptions {...modalComponentProps} />
        ),
    });
  };

  if (mergedTriggerProps.CustomTrigger)
    return (
      <button
        onClick={openSelectModalHandler}
        disabled={mergedTriggerProps.disabled || mergedTriggerProps.loading}
        className="text-left"
      >
        {mergedTriggerProps.CustomTrigger}
      </button>
    );

  return (
    <FloatingLabel
      htmlFor={modalKey}
      variant={mergedTriggerProps.variant}
      role="combobox"
      placeholder={mergedTriggerProps.placeholder}
      description={mergedTriggerProps.description ?? ''}
      isInvalid={mergedTriggerProps.isInvalid}
      disabled={mergedTriggerProps.disabled || mergedTriggerProps.loading}
      onClick={openSelectModalHandler}
      classNames={mergedTriggerProps.classNames}
      triggerClassName={mergedTriggerProps.className}
      endContent={
        <div
          className={twMerge(
            'ml-2 flex w-[22px] shrink-0 items-center',
            mergedTriggerProps.classNames?.icon,
          )}
        >
          {mergedTriggerProps.loading ? (
            <Spinner size="sm" className="w-full" />
          ) : (
            <ChevronIcon className="w-full rotate-90 text-base-300" />
          )}
        </div>
      }
      className={mergedTriggerProps.className ?? ''}
    >
      <button
        disabled={mergedTriggerProps.disabled || mergedTriggerProps.loading}
        className="peer absolute left-0 top-0 hidden h-full w-full min-w-0 appearance-none overflow-hidden border-0 bg-transparent p-0 text-inherit outline-none typography-16r disabled:cursor-not-allowed"
      >
        {valueLabel?.label ?? ''}
      </button>

      <div
        className={twMerge(
          'flex w-full flex-1 items-center overflow-hidden text-left typography-16r',
          Boolean(mergedTriggerProps.placeholder) && 'absolute bottom-0 left-0',
        )}
      >
        {renderTriggerValue ? (
          <div className="flex-1 overflow-hidden">
            {renderTriggerValue(
              value as SelectOption<TOption> & SelectOption<TOption>[],
            )}
          </div>
        ) : (
          value && <p className="flex-1 truncate">{valueLabel?.label}</p>
        )}
      </div>
    </FloatingLabel>
  );
};
